/**
 * useFullscreenPortalHost — où poser une surface pleine page pour qu'elle
 * s'affiche en plein écran
 *
 * En plein écran, le navigateur ne peint que le sous-arbre de
 * `document.fullscreenElement` : une modale portalisée dans `document.body`
 * existe dans le DOM mais n'apparaît jamais. Le conteneur retourné est donc
 * l'élément plein écran quand la surface s'ouvre pendant le plein écran, et
 * `document.body` sinon.
 *
 * Contrairement à `usePortalContainer`, qui suit le plein écran pour des
 * flottants sans état, le conteneur est **arrêté à l'ouverture et ne bouge
 * plus** : une modale porte un formulaire, et changer son conteneur de portail
 * en cours de route démonterait tout le sous-arbre React — la saisie serait
 * perdue. Une surface ouverte reste donc là où elle a été montée, y compris si
 * l'utilisateur quitte le plein écran (touche Échap) pendant qu'elle est là :
 * elle reste alors peinte au-dessus, en `position: fixed`.
 *
 * La spécification Fullscreen impose `transform: none` à l'élément plein écran :
 * un enfant en `position: fixed` y reste positionné par rapport au viewport, et
 * aucun `overflow` d'ancêtre ne le rogne.
 */

import { useRef } from 'react';

const resolveHost = (): HTMLElement => {
  const fullscreen = document.fullscreenElement;
  return fullscreen instanceof HTMLElement ? fullscreen : document.body;
};

/**
 * @param open `true` quand la surface est montée ; le conteneur est choisi à ce
 *             moment-là, puis conservé jusqu'à la fermeture.
 */
export function useFullscreenPortalHost(open: boolean): HTMLElement {
  // Un ref, et non un état : le conteneur doit être connu dès le premier rendu.
  // Le décider dans un effet monterait la surface dans `body` le temps d'une
  // image, puis la déplacerait — soit un démontage du formulaire.
  const hostRef = useRef<HTMLElement | null>(null);

  if (!open) {
    hostRef.current = null;
  } else if (!hostRef.current) {
    hostRef.current = resolveHost();
  }

  return hostRef.current ?? document.body;
}

export default useFullscreenPortalHost;
